import { getClient } from '../core/global-config.js';
import { createAndWaitTask } from '../core/index.js';

export interface AlibabaWanx21T2vTurboParams {
  /**
   * 输入参数
   */
  input: {
    /**
     * 视频生成提示词，长度不超过800字符
     */
    prompt: string;
  };
  /**
   * 生成参数
   */
  parameters?: {
    /**
     * 视频尺寸，支持480P/720P档位分辨率: 832*480, 480*832, 624*624, 1280*720, 720*1280, 960*960, 1088*832, 832*1088，默认1280*720
     */
    size?: string;
    /**
     * 视频时长，固定为5秒，不可修改
     */
    duration?: number;
    /**
     * 随机种子，范围0-2147483647
     */
    seed?: number;
    /**
     * 是否启用提示词扩展，默认true
     */
    prompt_extend?: boolean;
    /**
     * 是否添加水印，默认false
     */
    watermark?: boolean;
    /**
     * 负向提示词
     */
    negative_prompt?: string;
  };
}

export interface AlibabaWanx21T2vTurboResource {
  type: string;
  url: string;
  size?: number;
  jobId?: string;
  [key: string]: any;
}

/**
 * alibaba_wanx21_t2v_turbo
 *
 * Alibaba Wanx2.1 T2V Turbo 文本生成视频接口
 * 
 * Model: wanx2.1-t2v-turbo (固定模型)
 * Features: 文本生成视频功能
 * Duration: 固定5秒（不可修改）
 * Resolution: 支持480P/720P档位多种分辨率（默认1280*720）
 * Advanced: 支持提示词、反向提示词、提示词扩展、水印设置、随机种子控制
 * 
 * API Documentation: https://help.aliyun.com/zh/model-studio/text-to-video-api-reference
 *
 * @example
 * ```typescript
 * import { initSeacloud, alibabaWanx21T2vTurbo } from 'seacloud-sdk';
 *
 * initSeacloud('your-api-key');
 *
 * const result = await alibabaWanx21T2vTurbo({
 *   // 参数见 AlibabaWanx21T2vTurboParams 接口定义
 * });
 *
 * console.log(result); // 返回生成的资源列表
 * ```
 *
 * @param params 请求参数，详见 AlibabaWanx21T2vTurboParams 接口定义
 * @returns 生成的资源列表
 */
export async function alibabaWanx21T2vTurbo(
  params: AlibabaWanx21T2vTurboParams,
): Promise<AlibabaWanx21T2vTurboResource[]> {
  const client = await getClient();

  const pollingOptions = {
    intervalMs: 5000,
    maxAttempts: 120,
  };

  const result = await createAndWaitTask(
    client,
    '/model/v1/generation',
    {
      model: 'alibaba_wanx21_t2v_turbo',
      input: [{ params }],
    },
    pollingOptions,
  );

  const resources: AlibabaWanx21T2vTurboResource[] = [];

  if (result.output) {
    for (const item of result.output) {
      if (item.content) {
        for (const resource of item.content) {
          resources.push({
            type: resource.type || 'unknown',
            url: resource.url || '',
            size: resource.size,
            jobId: resource.jobId,
            ...resource,
          });
        }
      }
    }
  }

  return resources;
}
